import { FC, ReactNode, createContext, useEffect, useReducer } from 'react';
import { UserAuth } from 'src/models/userAuth';
import axios from 'src/utils/axios';
import PropTypes from 'prop-types';
import { firebaseAppQP, firestoreQP } from 'src/utils/firebaseQP';
import { usersByEmailOrDU } from 'src/slices/auth/authAction';
import { useDispatch } from 'src/store';
import { SnackbarUtils } from "src/utils/sncakbarUtilsConfigurator";
import { useTranslation } from 'react-i18next';
import { FIREBASE_ERRORS } from 'src/utils/constants';
import { useNavigate } from 'react-router-dom';
import axiosInst from 'src/utils/axiosCreate';
import { setSelectedProducer, setProducersSettings, clearAuthState } from 'src/slices/auth/authSlice';
import { rolesApp } from 'src/utils/rolesConstants';

interface AuthState {
  isInitialized: boolean;
  isAuthenticated: boolean;
  user: UserAuth | null;
}

interface AuthContextValue extends AuthState {
  method: 'JWT';
  login: (email: string, password: string) => Promise<void>;
  logout: () => void;
  register: (email: string, name: string, password: string) => Promise<void>;
}

interface AuthProviderProps {
  children: ReactNode;
}

type InitializeAction = {
  type: 'INITIALIZE';
  payload: {
    isAuthenticated: boolean;
    user: any | null;
  };
};

type LoginAction = {
  type: 'LOGIN';
  payload: {
    user: any;
  };
};

type LogoutAction = {
  type: 'LOGOUT';
};

type RegisterAction = {
  type: 'REGISTER';
  payload: {
    user: UserAuth;
  };
};

type Action = InitializeAction | LoginAction | LogoutAction | RegisterAction;

const initialAuthState: AuthState = {
  isAuthenticated: false,
  isInitialized: false,
  user: null
};

const setSession = (accessToken: string | null, email?: string): void => {
  if (accessToken) {
    localStorage.setItem('accessToken', accessToken);
    localStorage.setItem('userEmail', email);
    axios.defaults.headers.common.Authorization = `Bearer ${accessToken}`;
  } else {
    localStorage.removeItem('accessToken');
    localStorage.removeItem('userEmail');
    delete axios.defaults.headers.common.Authorization;
  }
};

const handlers: Record<
  string,
  (state: AuthState, action: Action) => AuthState
> = {
  INITIALIZE: (state: AuthState, action: InitializeAction): AuthState => {
    const { isAuthenticated, user } = action.payload;

    return {
      ...state,
      isAuthenticated,
      isInitialized: true,
      user
    };
  },
  LOGIN: (state: AuthState, action: LoginAction): AuthState => {
    const { user } = action.payload;

    return {
      ...state,
      isAuthenticated: true,
      user
    };
  },
  LOGOUT: (state: AuthState): AuthState => ({
    ...state,
    isAuthenticated: false,
    user: null
  }),
  REGISTER: (state: AuthState, action: RegisterAction): AuthState => {
    const { user } = action.payload;

    return {
      ...state,
      isAuthenticated: true,
      user
    };
  }
};

const reducer = (state: AuthState, action: Action): AuthState =>
  handlers[action.type] ? handlers[action.type](state, action) : state;

const AuthContext = createContext<AuthContextValue>({
  ...initialAuthState,
  method: 'JWT',
  login: () => Promise.resolve(),
  logout: () => Promise.resolve(),
  register: () => Promise.resolve()
});

export const AuthProvider: FC<AuthProviderProps> = (props) => {
  const { children } = props;
  const [state, dispatch] = useReducer(reducer, initialAuthState);
  const reduxDispatch = useDispatch();
  const { t }: { t: any } = useTranslation();
  const navigate = useNavigate();


  useEffect(() => {
    const initialize = async (): Promise<void> => {
      try {
        const accessToken = window.localStorage.getItem('accessToken');
        const email = window.localStorage.getItem('userEmail');
        if (accessToken) {
          setSession(accessToken, email);
          // valido que este el usuario..
          const userByDnuOrEmail = await reduxDispatch(
            usersByEmailOrDU(email, accessToken)
          );

          dispatch({
            type: 'INITIALIZE',
            payload: {
              isAuthenticated: true,
              user: userByDnuOrEmail.data.users[0]
            }
          });

          // Restaurar productoras desde sessionStorage
          const savedProducersConfig = sessionStorage.getItem('producersConfig');
          const savedSelectedProducer = sessionStorage.getItem('selectedProducer');
          
          if (savedProducersConfig) {
            // Caso 1: Hay datos en sessionStorage (misma pestaña o refresh)
            try {
              const { producers, isMultiproducer } = JSON.parse(savedProducersConfig);
              reduxDispatch(setProducersSettings({ producers, isMultiproducer }));
              
              if (savedSelectedProducer) {
                const selectedProducer = JSON.parse(savedSelectedProducer);
                reduxDispatch(setSelectedProducer(selectedProducer));
              } else if (Array.isArray(producers) && producers.length > 0) {
                if (isMultiproducer) {
                  reduxDispatch(setSelectedProducer(null));
                } else {
                  reduxDispatch(setSelectedProducer(producers[0]));
                }
              } else {
                reduxDispatch(setSelectedProducer(null));
              }
            } catch (error) {
              console.error('Error restoring producers from session:', error);
            }
          } else {
            // Caso 2: No hay datos en sessionStorage (nueva pestaña)
            // Reconstruir configuración desde los datos del usuario
            const userData = userByDnuOrEmail.data.users[0];
            const producerMap = new Map();
            
            if (userData.proRoles && Array.isArray(userData.proRoles)) {
              userData.proRoles.forEach((role: any) => {
                if (role.producerId && Array.isArray(role.producerId)) {
                  role.producerId.forEach((producer: any) => {
                    if (producer && producer.id && !producerMap.has(producer.id)) {
                      producerMap.set(producer.id, {
                        id: producer.id.toString(),
                        name: producer.name,
                        isActive: true
                      });
                    }
                  });
                }
              });
            }
            
            const uniqueProducers = Array.from(producerMap.values());
            
            // Leer isMultiProducer desde Firestore
            let isMultiproducer = false;
            try {
              const producersSettingsDoc = await firestoreQP.collection('settings').doc('producers').get();
              if (producersSettingsDoc.exists) {
                const data = producersSettingsDoc.data();
                if (typeof data.isMultiProducer === 'boolean') {
                  isMultiproducer = data.isMultiProducer;
                }
              }
            } catch (error) {
              console.warn('Could not fetch isMultiProducer from Firebase:', error);
            }
            
            // Configurar Redux y sessionStorage
            if (uniqueProducers.length > 0) {
              reduxDispatch(setProducersSettings({ producers: uniqueProducers, isMultiproducer }));
              sessionStorage.setItem('producersConfig', JSON.stringify({ producers: uniqueProducers, isMultiproducer }));
              
              if (isMultiproducer) {
                // En multiproductora, dejar null para que navegue a /select-producer
                reduxDispatch(setSelectedProducer(null));
              } else {
                // En single-producer, seleccionar la primera
                reduxDispatch(setSelectedProducer(uniqueProducers[0]));
              }
            } else {
              reduxDispatch(setProducersSettings({ producers: [], isMultiproducer: false }));
              reduxDispatch(setSelectedProducer(null));
            }
          }
        } else {
          dispatch({
            type: 'INITIALIZE',
            payload: {
              isAuthenticated: false,
              user: null
            }
          });
        }
      } catch (err) {
        dispatch({
          type: 'INITIALIZE',
          payload: {
            isAuthenticated: false,
            user: null
          }
        });
      }
    };

    initialize();
  }, []);

  useEffect(() => {
    axiosInst.interceptors.response.use(
      response => response,
      async (error) => {
        if (error.response?.status === 401) {
          reduxDispatch(clearAuthState());
          await logout();
          navigate('/account/login');
        }
        return Promise.reject(error);
      }
    );
  }, [navigate]);

  const login = async (email: string, password: string): Promise<void> => {
    try {
      const loginFirebase = await firebaseAppQP
        .auth()
        .signInWithEmailAndPassword(email, password);

      if (!!loginFirebase && !loginFirebase.user && !!loginFirebase.user.email) {
        return SnackbarUtils.error(t('Login failed'));
      }

      const token = await loginFirebase.user.getIdToken();
      setSession(token, email);
      const userByDnuOrEmail = await reduxDispatch(usersByEmailOrDU(email));

      if (!userByDnuOrEmail.data.users[0].proRoles && userByDnuOrEmail.data.users[0].proRoles === undefined) {
        return SnackbarUtils.error(t('You do not have permissions to enter the app'));
      }

      if (
        !userByDnuOrEmail ||
        !userByDnuOrEmail.data ||
        !userByDnuOrEmail.data.users ||
        !userByDnuOrEmail.data.users[0]
      ) {
        return SnackbarUtils.error(t('Login failed'));
      }
      // END COMMENT

      dispatch({
        type: 'LOGIN',
        payload: {
          // Se reemplaza, user: loginFirebase ya que no traía los roles de usuario que necesitamos.
          user: userByDnuOrEmail.data.users[0]
        }
      });

      const userData = userByDnuOrEmail.data.users[0];
      
      // Extraer productoras únicas desde proRoles
      const producerMap = new Map();
      
      if (userData.proRoles && Array.isArray(userData.proRoles)) {
        userData.proRoles.forEach((role: any) => {
          if (role.producerId && Array.isArray(role.producerId)) {
            role.producerId.forEach((producer: any) => {
              if (producer && producer.id && !producerMap.has(producer.id)) {
                producerMap.set(producer.id, {
                  id: producer.id.toString(),
                  name: producer.name,
                  isActive: true
                });
              }
            });
          }
        });
      }
      
      const uniqueProducers = Array.from(producerMap.values());
      
      // Leer isMultiProducer desde Firebase settings/producers
      // IMPORTANTE: El campo en Firestore es "isMultiProducer" (con P mayúscula)
      // Si el documento NO existe, no es multiproductora (isMultiproducer = false)
      let isMultiproducer = false;
      
      try {
        const producersSettingsDoc = await firestoreQP.collection('settings').doc('producers').get();
        if (producersSettingsDoc.exists) {
          const data = producersSettingsDoc.data();
          if (typeof data.isMultiProducer === 'boolean') {
            isMultiproducer = data.isMultiProducer;
          }
        }
        // Si el documento no existe, isMultiproducer se mantiene en false
      } catch (error) {
        console.warn('Could not fetch isMultiProducer from Firebase:', error);
      }
      
      if (uniqueProducers.length > 0) {
        reduxDispatch(setProducersSettings({ producers: uniqueProducers, isMultiproducer }));
        sessionStorage.setItem('producersConfig', JSON.stringify({ producers: uniqueProducers, isMultiproducer }));
        
    
        if (isMultiproducer) {
          //Roles con funcionalidad en el BO
          const rolesPermissions = [rolesApp.SUPERADMIN, rolesApp.ADMIN, rolesApp.PRODUCTOR, rolesApp.POS];
          //Filtralmos los roles que tengan funcionalidades dentro del BO
          const validRoles = userByDnuOrEmail?.data?.users[0]?.proRoles?.filter((role) => rolesPermissions.includes(role.id));
          //filtramos las productoras que tengan por lo menos un rol con permisos en el BO
          const producersValid = uniqueProducers.filter((producer) => validRoles.some((role) => role.producerId?.some((producerId) => producerId?.id?.toString() === producer?.id?.toString())));

          if (producersValid.length === 1) {
            reduxDispatch(setSelectedProducer(producersValid[0]));
          } else {
            // Si hay múltiples productoras, verificar si hay una guardada
            const savedProducer = sessionStorage.getItem('selectedProducer');
            
            if (savedProducer) {
              try {
                const producer = JSON.parse(savedProducer);
                const isValidProducer = uniqueProducers.some(
                  (p: any) => p.id === producer.id
                );
                
                if (isValidProducer) {
                  reduxDispatch(setSelectedProducer(producer));
                } else {
                  sessionStorage.removeItem('selectedProducer');
                  reduxDispatch(setSelectedProducer(null));
                  navigate('/select-producer');
                  return;
                }
              } catch (error) {
                console.error('Error parsing saved producer:', error);
                sessionStorage.removeItem('selectedProducer');
                reduxDispatch(setSelectedProducer(null));
                navigate('/select-producer');
                return;
              }
            } else {
              reduxDispatch(setSelectedProducer(null));
              navigate('/select-producer');
              return;
            }
          }
        } else {
          reduxDispatch(setSelectedProducer(uniqueProducers[0]));
        }
      } else {
        reduxDispatch(setProducersSettings({ producers: [], isMultiproducer }));
        sessionStorage.setItem('producersConfig', JSON.stringify({ producers: [], isMultiproducer }));
        reduxDispatch(setSelectedProducer(null));
      }

      // Navegar a /dashboard/management y dejar que ManagementIndexRedirect
      // determine la ruta correcta basándose en los roles filtrados por productora
      if (userByDnuOrEmail.data.users[0].proRoles?.length > 0) {
        navigate('/dashboard/management');
      }
    } catch (e) {
      let errorMessage = t('Login failed');
      if (e && e.code) {
        switch (e.code) {
          case FIREBASE_ERRORS.INVALID_EMAIL:
            errorMessage = t("The email address is not valid. Please check and try again.");
            break;
          case FIREBASE_ERRORS.USER_DISABLED:
            errorMessage = t("This user account has been disabled. Please contact support.");
            break;
          case FIREBASE_ERRORS.USER_NOT_FOUND:
            errorMessage = t("No account found with this email. Please register first.");
            break;
          case FIREBASE_ERRORS.WRONG_PASSWORD:
            errorMessage = t("Incorrect password. Please try again.");
            break;
          default:
            errorMessage = t('Login failed');
        }
      }
      return SnackbarUtils.error(errorMessage);
    }
  };

  const logout = async (): Promise<void> => {
    try {
      await firebaseAppQP.auth().signOut();
    } catch (err) {
      console.error('Firebase logout error:', err);
    }
    reduxDispatch(clearAuthState());
    setSession(null);
    dispatch({ type: 'LOGOUT' });
  };

  const register = async (
    email: string,
    name: string,
    password: string
  ): Promise<void> => {
    const response = await axios.post<{ accessToken: string; user: UserAuth }>(
      '/api/account/register',
      {
        email,
        name,
        password
      }
    );
    const { accessToken, user } = response.data;

    window.localStorage.setItem('accessToken', accessToken);
    dispatch({
      type: 'REGISTER',
      payload: {
        user
      }
    });
  };

  return (
    <AuthContext.Provider
      value={{
        ...state,
        method: 'JWT',
        login,
        logout,
        register
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

AuthProvider.propTypes = {
  children: PropTypes.node.isRequired as PropTypes.Validator<ReactNode>
};

export default AuthContext;
